class Project {
  constructor(data = {}) {
    this.id = data.id || data._id || null;
    this._id = data._id || data.id || null;
    this.projectId = data.projectId || `PRJ${Date.now()}`;
    
    // Map frontend fields to backend fields
    this.name = data.name || data.projectName || '';
    this.projectName = data.projectName || data.name || '';
    
    this.description = data.description || '';
    
    // Map client fields
    this.client = data.client || data.clientName || '';
    this.clientName = data.clientName || data.client || '';
    
    // projectManager should be ObjectId or null (not a string name)
    this.projectManager = data.projectManager || null;
    
    // Map status - backend uses 'Active' instead of 'In Progress'
    this.status = this.mapStatus(data.status || 'Planning');
    
    this.priority = data.priority || 'Medium';
    this.startDate = data.startDate || '';
    this.endDate = data.endDate || '';
    
    // Map budget fields
    this.budget = data.budget || 0;
    this.costIncurred = data.costIncurred || data.spentAmount || 0;
    this.spentAmount = data.spentAmount || data.costIncurred || 0;
    
    // Map progress fields
    this.completionPercentage = data.completionPercentage || data.progress || 0;
    this.progress = data.progress || data.completionPercentage || 0;
    
    this.teamMembers = data.teamMembers || [];
    this.tags = data.tags || [];
    this.attachments = data.attachments || [];
    this.createdAt = data.createdAt || new Date().toISOString();
  }

  // Map frontend status to backend status
  mapStatus(status) {
    const statusMap = {
      'In Progress': 'Active',
      'Active': 'Active',
      'Planning': 'Planning',
      'On Hold': 'On Hold',
      'Completed': 'Completed',
      'Cancelled': 'Cancelled'
    };
    return statusMap[status] || status;
  }

  // Map backend status to frontend status for display
  getDisplayStatus() {
    const statusMap = {
      'Active': 'In Progress',
      'Planning': 'Planning',
      'On Hold': 'On Hold',
      'Completed': 'Completed',
      'Cancelled': 'Cancelled'
    };
    return statusMap[this.status] || this.status;
  }

  getStatusColor() {
    const colors = {
      'Planning': '#3b82f6',
      'Active': '#f59e0b',
      'In Progress': '#f59e0b',
      'On Hold': '#6b7280',
      'Completed': '#10b981',
      'Cancelled': '#ef4444'
    };
    return colors[this.status] || colors[this.getDisplayStatus()] || '#6b7280';
  }

  getPriorityColor() {
    const colors = { 
      'High': '#ef4444',
      'Critical': '#dc2626',
      'Medium': '#f59e0b', 
      'Low': '#3b82f6' 
    };
    return colors[this.priority] || '#6b7280';
  }

  getBudgetStatus() {
    const spent = this.spentAmount || this.costIncurred || 0;
    if (spent > this.budget) return 'Over Budget';
    if (spent >= this.budget * 0.9) return 'Near Budget';
    return 'Within Budget';
  }

  getDaysRemaining() {
    if (!this.endDate) return 0;
    const end = new Date(this.endDate);
    const now = new Date();
    const diff = Math.ceil((end - now) / (1000 * 60 * 60 * 24));
    return diff;
  }

  validate() {
    const errors = [];
    
    // Use 'name' for backend validation
    if (!this.name?.trim() && !this.projectName?.trim()) {
      errors.push('Project name is required');
    }
    
    // Use 'client' for backend validation
    if (!this.client?.trim() && !this.clientName?.trim()) {
      errors.push('Client name is required');
    }
    
    if (!this.startDate) {
      errors.push('Start date is required');
    }
    
    if (!this.endDate) {
      errors.push('End date is required');
    }
    
    if (this.startDate && this.endDate && new Date(this.startDate) > new Date(this.endDate)) {
      errors.push('End date must be after start date');
    }
    
    if (this.budget < 0) {
      errors.push('Budget cannot be negative');
    }
    
    // Validate status enum
    const validStatuses = ['Planning', 'Active', 'On Hold', 'Completed', 'Cancelled'];
    if (!validStatuses.includes(this.status)) {
      errors.push(`Status must be one of: ${validStatuses.join(', ')}`);
    }
    
    // Validate priority enum
    const validPriorities = ['Low', 'Medium', 'High', 'Critical'];
    if (!validPriorities.includes(this.priority)) {
      errors.push(`Priority must be one of: ${validPriorities.join(', ')}`);
    }
    
    return { isValid: errors.length === 0, errors };
  }

  // Convert to backend API format
  toJSON() {
    return {
      name: this.name || this.projectName,
      description: this.description,
      client: this.client || this.clientName,
      projectManager: this.projectManager || undefined, // Don't send if null
      status: this.status,
      priority: this.priority,
      startDate: this.startDate,
      endDate: this.endDate,
      budget: this.budget,
      costIncurred: this.costIncurred || this.spentAmount,
      completionPercentage: this.completionPercentage || this.progress,
      teamMembers: this.teamMembers,
      tags: this.tags,
      attachments: this.attachments
    };
  }

  static fromJSON(json) {
    return new Project(json);
  }
}

export default Project;